import { Phone, Mail, MessageCircle, Clock, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import { FadeUp, MaskLine } from "@/components/motion";
import { CONTACT, WHATSAPP_DEFAULT } from "@/data/content";

export default function Contact() {
  const cards = [
    {
      icon: Phone, title: "Call us",
      lines: [
        { label: CONTACT.phonePrimary, href: `tel:${CONTACT.phonePrimaryRaw}`, testid: "contact-phone-primary" },
        { label: `${CONTACT.phoneMobile} (mobile)`, href: `tel:${CONTACT.phoneMobileRaw}`, testid: "contact-phone-mobile" },
      ],
      note: "Fastest for urgent moves and same-week bookings.",
    },
    {
      icon: MessageCircle, title: "WhatsApp",
      lines: [{ label: "Start a chat", href: WHATSAPP_DEFAULT, testid: "contact-whatsapp", external: true }],
      note: "Send photos of your load for a sharper quote.",
    },
    {
      icon: Mail, title: "Email",
      lines: [{ label: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" }],
      note: "Attach inventories, floor plans or agent checklists.",
    },
  ];

  return (
    <main className="pt-20">
      <SEO
        title="Contact Us"
        description="Contact NextMove Removalists on 1300 429 532 or 0426 382 669, WhatsApp, or email. Open 7 days, 6am–9pm AEST, Australia-wide."
        path="/contact"
      />
      <section className="bg-navy grain py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">Contact</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white">
            <MaskLine delay={0.1}>Talk to the team that</MaskLine>
            <MaskLine delay={0.22}>drives <span className="text-amber-400">the trucks.</span></MaskLine>
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-6 flex items-center gap-2 text-sm text-slate-300">
              <Clock className="h-4 w-4 text-amber-500" /> {CONTACT.hours} · Australia-wide
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 sm:py-24 grid gap-12 lg:grid-cols-2">
        <div className="space-y-6">
          {cards.map((c, i) => (
            <FadeUp key={c.title} delay={i * 0.08} className="card-lift rounded-2xl border border-slate-200 bg-white p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-amber-400">
                  <c.icon className="h-5 w-5" />
                </span>
                <h2 className="font-display text-xl font-bold text-navy">{c.title}</h2>
              </div>
              <div className="mt-4 space-y-1.5">
                {c.lines.map((l) => (
                  <a key={l.testid} href={l.href} data-testid={l.testid}
                    target={l.external ? "_blank" : undefined} rel={l.external ? "noopener noreferrer" : undefined}
                    className="block font-display text-lg font-bold text-navy hover:text-amber-600 transition-colors duration-150">
                    {l.label}
                  </a>
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-500">{c.note}</p>
            </FadeUp>
          ))}
          <FadeUp delay={0.3} className="rounded-2xl bg-slate-100 p-8">
            <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
              <MapPin className="h-4 w-4" /> Coverage
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              A service-area business operating across Australia, one point of contact
              from quote to delivery. ABN {CONTACT.abn}.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.15}>
          <h2 className="mb-6 font-display text-2xl font-bold text-navy">Or skip straight to a quote</h2>
          <QuoteForm />
        </FadeUp>
      </section>
    </main>
  );
}
